import { useEffect } from 'react';
import { selectActiveClip } from '~/features/clips/selectors';
import { buildFrames, buildFramesFromBlobs } from '~/features/frames/pipeline';
import { selectColDividers, selectRowDividers } from '~/features/grid/selectors';
import { useStore } from '~/store';
import { pickSettings } from '~/store/partialize';
import type { AlignmentMode, Clip, Frame, MatchResult } from '~/types/pipeline';
import { downloadAtlas } from './download';
import { applyLayout } from './layout';
import { buildManifest } from './manifest';
import { packAtlas } from './pack';

const EXPORT_EVENT = 'spriteer:export';
const EXPORT_ALL_EVENT = 'spriteer:export-all';

function sanitizeFileName(name: string): string {
  return name.replace(/[^a-z0-9._-]+/gi, '_').replace(/^_+|_+$/g, '') || 'clip';
}

export function buildFramesForClip(
  state: ReturnType<typeof useStore.getState>,
  cleaned: ImageData,
  clip: Clip,
  matches: MatchResult[] | null,
  alignmentMode: AlignmentMode = clip.alignmentMode,
): Frame[] {
  const cropRect = state.grid.cropRect;
  if (!cropRect) return [];
  const activeMatches = alignmentMode === 'reference-point' ? matches : null;
  const rows = selectRowDividers(state);
  if (state.pin.framingMode === 'pin') {
    if (clip.source.kind !== 'blobs') return [];
    const blobs = state.pin.blobs;
    if (!blobs) return [];
    return buildFramesFromBlobs({
      cleaned,
      cropRect,
      blobs,
      overrides: state.pin.overrides,
      alignmentMode,
      blobIndices: clip.source.blobs,
      ...(activeMatches ? { matches: activeMatches } : {}),
    });
  }

  if (clip.source.kind !== 'cells') return [];
  const cols = selectColDividers(state);
  if (cols.length < 2 || rows.length < 2) return [];
  return buildFrames({
    cleaned,
    cropRect,
    rows,
    cols,
    alignmentMode,
    pivot: state.frames.pivot,
    cells: clip.source.cells,
    ...(activeMatches ? { matches: activeMatches } : {}),
  });
}

export async function exportClip(clip: Clip, matches: MatchResult[] | null): Promise<void> {
  const s = useStore.getState();
  const cleaned = s.workflow.committedCleaned;
  if (!cleaned || !s.grid.cropRect) {
    s.setExportError('Continue to the sprite step and define a crop region before exporting.');
    return;
  }

  const frames = buildFramesForClip(s, cleaned, clip, matches);
  if (frames.length === 0) {
    s.setExportError(`Clip "${clip.name}" has no frames.`);
    return;
  }

  const laidOutFrames = applyLayout(frames, s.atlas.layoutMode);
  const { canvas, atlas } = packAtlas(laidOutFrames, cleaned, {
    padding: 1,
    appName: 'Spriteer',
    appVersion: '0.2.0',
  });
  const manifest = buildManifest({
    fileName: clip.name,
    settings: pickSettings(s) as unknown as Record<string, unknown>,
  });
  await downloadAtlas({
    canvas,
    atlas,
    manifest,
    fileName: sanitizeFileName(clip.name),
  });
}

export async function exportAtlas(): Promise<void> {
  const s = useStore.getState();
  s.setExporting(true);
  s.setExportError(null);
  try {
    const clip = selectActiveClip(s);
    if (!clip) {
      s.setExportError('Select an active clip before exporting.');
      return;
    }
    await exportClip(clip, s.referencePoint.results);
  } catch (err) {
    s.setExportError((err as Error).message);
  } finally {
    s.setExporting(false);
  }
}

export function triggerExport(): void {
  window.dispatchEvent(new CustomEvent(EXPORT_EVENT));
}

export function triggerExportAll(): void {
  window.dispatchEvent(new CustomEvent(EXPORT_ALL_EVENT));
}

/** Mount once at app root so the export keyboard shortcut and toolbar share a single handler. */
export function useExportListener(): void {
  useEffect(() => {
    const exportHandler = () => {
      void exportAtlas();
    };
    const exportAllHandler = () => {
      void import('./exportAll').then(({ exportAllClips }) => exportAllClips());
    };

    window.addEventListener(EXPORT_EVENT, exportHandler);
    window.addEventListener(EXPORT_ALL_EVENT, exportAllHandler);
    return () => {
      window.removeEventListener(EXPORT_EVENT, exportHandler);
      window.removeEventListener(EXPORT_ALL_EVENT, exportAllHandler);
    };
  }, []);
}
